/**
 * @module features/clients/components/ClientInspectorDrawer
 * @description Slide-over sheet / mobile bottom sheet inspector for Client Organizations.
 * Built to match DeviceInspectorDrawer and ViewUserModal design standards.
 * Features 3 segmented tabs: Overview, Linked Users, and Operational Settings.
 */
import * as React from "react";
import {
  Mail,
  MapPin,
  UserCheck,
  UserPlus,
  ShieldAlert,
  Copy,
  Check,
  Trash2,
  Edit,
  Users,
  AlertTriangle,
  ShieldCheck,
  Clock,
  Layers,
  X,
  Search,
} from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ClientDetail } from "../types";
import { ClientQuotaGauge } from "./ClientQuotaGauge";
import { useClientDetail, useClientUsers, useUnlinkUser } from "../api/clientQueries";

export interface ClientInspectorDrawerProps {
  clientId: string | null;
  open: boolean;
  onClose: () => void;
  onEdit: (client: ClientDetail) => void;
  onLinkUsers: (client: ClientDetail) => void;
  onToggleStatus: (client: ClientDetail) => void;
  onDelete: (client: ClientDetail) => void;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-border/50 last:border-0">
      <dt className="text-xs text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-xs text-foreground text-right min-w-0 font-medium break-words">{children}</dd>
    </div>
  );
}

export function ClientInspectorDrawer({
  clientId,
  open,
  onClose,
  onEdit,
  onLinkUsers,
  onToggleStatus,
  onDelete,
}: ClientInspectorDrawerProps) {
  const [activeTab, setActiveTab] = React.useState<"overview" | "users" | "settings">("overview");
  const [copiedId, setCopiedId] = React.useState(false);
  const [unlinkingUserId, setUnlinkingUserId] = React.useState<string | null>(null);
  const [userSearchTerm, setUserSearchTerm] = React.useState("");

  const { data: client, isLoading, isError, refetch } = useClientDetail(clientId);
  const { data: linkedUsers, isLoading: isUsersLoading, refetch: refetchUsers } = useClientUsers(
    clientId
  );
  const unlinkMutation = useUnlinkUser();

  const filteredLinkedUsers = React.useMemo(() => {
    if (!linkedUsers) return [];
    if (!userSearchTerm.trim()) return linkedUsers;
    const q = userSearchTerm.toLowerCase().trim();
    return linkedUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email_id.toLowerCase().includes(q)
    );
  }, [linkedUsers, userSearchTerm]);

  const handleCopyId = () => {
    if (!client) return;
    try {
      navigator.clipboard.writeText(client.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      // Ignore clipboard error
    }
  };

  const handleUnlink = async (userId: string) => {
    if (!clientId) return;
    setUnlinkingUserId(userId);
    try {
      await unlinkMutation.mutateAsync({ clientId, userId });
      refetchUsers();
      refetch();
    } catch (err) {
      console.error("Failed to unlink user:", err);
    } finally {
      setUnlinkingUserId(null);
    }
  };

  const statusPill =
    client?.status === "active"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
      : client?.status === "suspended"
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
      : "bg-muted text-muted-foreground border-border";

  const renderHeader = () => {
    if (isLoading) {
      return (
        <div className="p-3.5 sm:p-4 space-y-3 animate-pulse bg-card/60 dark:bg-zinc-950" aria-busy="true">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 sm:h-5 w-40 sm:w-48 rounded-md" />
                <Skeleton className="h-3 w-32 rounded-md" />
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Skeleton className="h-8 flex-1 rounded-lg" />
            <Skeleton className="h-8 flex-1 rounded-lg" />
            <Skeleton className="h-8 w-20 rounded-lg" />
          </div>
          <Skeleton className="h-9 w-full rounded-xl" />
        </div>
      );
    }

    if (isError || !client) {
      return (
        <div className="flex items-center justify-between p-3.5 sm:p-4 bg-card dark:bg-zinc-950">
          <h2 className="text-sm font-semibold text-foreground">Client Organization Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return (
      <div className="p-3.5 sm:p-4 space-y-2.5 bg-card/95 dark:bg-zinc-950 backdrop-blur-xs">
        {/* Top Row: Brand Logo, Org Title, Status Badge, Email, 1-Click Copyable ID, and Close Button */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-center gap-3 min-w-0">
            {client.logo_url ? (
              <img
                src={client.logo_url}
                alt={`${client.org_name} logo`}
                className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl object-contain border border-border/80 bg-white p-1 shrink-0 shadow-xs"
                loading="lazy"
              />
            ) : (
              <div
                className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl border border-border/80 flex items-center justify-center font-bold text-xs sm:text-sm shrink-0 shadow-xs"
                style={{
                  backgroundColor: `${client.theme_color}18`,
                  color: client.theme_color,
                }}
              >
                {client.org_name.substring(0, 2).toUpperCase()}
              </div>
            )}

            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h2 className="text-sm sm:text-base font-bold tracking-tight text-foreground truncate max-w-[170px] xs:max-w-[240px] sm:max-w-[320px]">
                  {client.org_name}
                </h2>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-mono font-medium px-2 py-0.5 rounded-full border capitalize shrink-0 ${statusPill}`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      client.status === "active"
                        ? "bg-emerald-500"
                        : client.status === "suspended"
                        ? "bg-amber-500"
                        : "bg-zinc-400"
                    }`}
                  />
                  {client.status}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mt-0.5">
                <span className="font-mono text-[11px] truncate max-w-[130px] xs:max-w-[190px] sm:max-w-[240px]">
                  {client.org_email_id}
                </span>
                <span className="opacity-40 hidden xs:inline">•</span>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground hover:text-foreground bg-muted/60 hover:bg-muted px-1.5 py-0.5 rounded border border-border/70 transition-colors group select-all shadow-2xs"
                  title="Click to copy Client Organization ID"
                >
                  <span className="font-semibold text-foreground">ID:</span>
                  <span className="font-mono tracking-tight">{client.id}</span>
                  {copiedId ? (
                    <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                  ) : (
                    <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100 shrink-0" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Close button at top-right (no wasted empty header bar) */}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:hover:bg-zinc-900 transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Close inspector"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Quick Action CTAs row in Header */}
        <div className="flex items-center gap-1.5 sm:gap-2 pt-1 border-t border-border/40">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(client)}
            className="h-8 text-xs gap-1.5 flex-1 shadow-2xs font-medium px-2 sm:px-3"
          >
            <Edit className="h-3.5 w-3.5 shrink-0" />
            <span>Edit<span className="hidden xs:inline"> Profile</span></span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onLinkUsers(client)}
            className="h-8 text-xs gap-1.5 flex-1 shadow-2xs font-medium px-2 sm:px-3"
          >
            <UserPlus className="h-3.5 w-3.5 shrink-0" />
            <span>Link<span className="hidden xs:inline"> Users</span></span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleStatus(client)}
            className="h-8 text-xs gap-1.5 text-muted-foreground hover:text-amber-600 dark:hover:text-amber-400 shadow-2xs font-medium px-2.5 sm:px-3"
            title={client.status === "active" ? "Suspend organization" : "Reactivate organization"}
          >
            <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
            <span>{client.status === "active" ? "Suspend" : "Activate"}</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(client)}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
            title="Delete organization"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Segmented Control Tabs (Integrated into sticky Header) */}
        <div className="flex items-center gap-1 p-1 bg-muted/70 dark:bg-zinc-900 rounded-xl border border-border/60 dark:border-zinc-800">
          {[
            { id: "overview" as const, label: "Overview", shortLabel: "Overview", icon: Layers },
            {
              id: "users" as const,
              label: "Linked Users",
              shortLabel: "Users",
              icon: Users,
              count: linkedUsers?.length ?? client.active_user_count,
            },
            {
              id: "settings" as const,
              label: "Settings & Audit",
              shortLabel: "Settings",
              icon: ShieldCheck,
            },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 min-h-[34px] ${
                  isActive
                    ? "bg-card dark:bg-zinc-800 text-foreground shadow-xs border border-border/40 dark:border-zinc-700"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline truncate">{tab.label}</span>
                <span className="sm:hidden truncate">{tab.shortLabel}</span>
                {tab.count !== undefined && (
                  <span
                    className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                      isActive
                        ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      header={renderHeader()}
      className="sm:w-[540px] md:w-[580px]"
      bodyClassName="p-3.5 sm:p-5"
    >
      {isLoading ? (
        <div className="space-y-4 animate-pulse" aria-busy="true">
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      ) : isError || !client ? (
        <div className="p-8 text-center space-y-4">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h3 className="text-base font-semibold text-foreground">
            Failed to load organization details
          </h3>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Unable to retrieve organizational attributes from the gateway.
          </p>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="text-xs">
            Retry Request
          </Button>
        </div>
      ) : (
        <div className="space-y-4 pb-6">
          {/* Tab 1: Overview */}
          {activeTab === "overview" && (
            <div className="space-y-4 animate-in fade-in-50 duration-150">
              {/* Seat Capacity Gauge */}
              <ClientQuotaGauge
                activeCount={client.active_user_count}
                maxLimit={client.max_user_limit}
              />

              {/* White-Label Brand Theme Preview */}
              <div className="p-4 rounded-2xl border border-border/70 dark:border-zinc-800 bg-card space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">Portal Brand Theme</span>
                  <div className="flex items-center gap-2">
                    <span
                      className="h-4 w-4 rounded-full ring-1 ring-border shadow-xs"
                      style={{ backgroundColor: client.theme_color }}
                    />
                    <span className="font-mono text-xs font-semibold text-foreground uppercase">
                      {client.theme_color}
                    </span>
                  </div>
                </div>

                {/* Live Portal Button Preview Bar */}
                <div
                  className="p-3 rounded-xl flex items-center justify-between text-xs text-white"
                  style={{ backgroundColor: client.theme_color }}
                >
                  <span className="font-semibold drop-shadow-xs">{client.org_name} Portal Theme</span>
                  <span className="text-[11px] bg-black/20 backdrop-blur-xs px-2 py-0.5 rounded-md font-mono">
                    Dynamic CSS Accent
                  </span>
                </div>
              </div>

              {/* Primary Contact Person */}
              <div className="p-4 rounded-2xl border border-border/70 dark:border-zinc-800 bg-card space-y-2">
                <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  Primary Contact Person
                </h4>
                <dl className="divide-y divide-border/50">
                  <DetailRow label="Full Name">{client.contact_person_name}</DetailRow>
                  <DetailRow label="Official Email">
                    <span className="font-mono">{client.contact_person_email}</span>
                  </DetailRow>
                  <DetailRow label="Direct Mobile">
                    <span className="font-mono">{client.contact_person_mobile_no}</span>
                  </DetailRow>
                  <DetailRow label="Office Contact">
                    <span className="font-mono">{client.org_contact_no}</span>
                  </DetailRow>
                </dl>
              </div>

              {/* Physical Headquarters Address */}
              <div className="p-4 rounded-2xl border border-border/70 dark:border-zinc-800 bg-card space-y-2">
                <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  Physical Headquarters
                </h4>
                <dl className="divide-y divide-border/50">
                  <DetailRow label="Street Address">{client.street}</DetailRow>
                  {client.landmark && <DetailRow label="Landmark">{client.landmark}</DetailRow>}
                  <DetailRow label="City & State">
                    {client.city}, {client.state}
                  </DetailRow>
                  <DetailRow label="Postal Code">
                    <span className="font-mono">{client.pincode}</span>
                  </DetailRow>
                  <DetailRow label="Country">{client.country || "India"}</DetailRow>
                </dl>
              </div>

              {/* Notification Targets */}
              <div className="p-4 rounded-2xl border border-border/70 dark:border-zinc-800 bg-card space-y-2">
                <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  Platform Notification Recipients
                </h4>
                <dl className="divide-y divide-border/50">
                  <DetailRow label="Alert Emails">
                    {client.notification_email_ids?.length ? (
                      client.notification_email_ids.join(", ")
                    ) : (
                      <span className="text-muted-foreground font-normal">None configured</span>
                    )}
                  </DetailRow>
                  <DetailRow label="SMS Phone Numbers">
                    {client.notification_mobile_nos?.length ? (
                      client.notification_mobile_nos.join(", ")
                    ) : (
                      <span className="text-muted-foreground font-normal">None configured</span>
                    )}
                  </DetailRow>
                </dl>
              </div>
            </div>
          )}

          {/* Tab 2: Linked Users */}
          {activeTab === "users" && (
            <div className="space-y-3.5 animate-in fade-in-50 duration-150">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Linked Accounts:{" "}
                  <strong className="text-foreground font-mono">{linkedUsers?.length ?? 0}</strong> of{" "}
                  <strong className="text-foreground font-mono">{client.max_user_limit}</strong> max seats
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onLinkUsers(client)}
                  className="h-8 text-xs gap-1.5"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Link User
                </Button>
              </div>

              {/* Instant client-side search (0 API calls) when organization has multiple linked members */}
              {linkedUsers && linkedUsers.length > 2 && (
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
                  <Input
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder="Filter linked members by name or email..."
                    className="pl-8.5 pr-8 h-8 text-xs bg-muted/30"
                  />
                  {userSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setUserSearchTerm("")}
                      className="absolute right-2.5 top-2 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="Clear filter"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}

              {isUsersLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="p-3 rounded-xl border border-border/60 bg-card flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-3.5 w-24" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </div>
                      <Skeleton className="h-7 w-16 rounded-md" />
                    </div>
                  ))}
                </div>
              ) : !linkedUsers || linkedUsers.length === 0 ? (
                <div className="p-8 rounded-2xl border border-dashed border-border text-center space-y-2.5 bg-card/40">
                  <Users className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="text-xs font-semibold text-foreground">No Users Linked Yet</p>
                  <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                    Link existing client-role accounts or invite new members to this organization.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => onLinkUsers(client)}
                    className="text-xs gap-1.5 mt-2"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Link First User
                  </Button>
                </div>
              ) : filteredLinkedUsers.length === 0 ? (
                <div className="p-6 rounded-2xl border border-border/80 bg-card/40 text-center space-y-1.5 text-xs text-muted-foreground">
                  <p className="font-semibold text-foreground">No linked members match filter</p>
                  <p>No members match &ldquo;{userSearchTerm}&rdquo;.</p>
                  <button
                    type="button"
                    onClick={() => setUserSearchTerm("")}
                    className="text-primary hover:underline font-medium text-xs pt-1"
                  >
                    Clear filter
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-border/60 rounded-2xl border border-border/80 bg-card overflow-hidden shadow-xs">
                  {filteredLinkedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="p-3 sm:p-3.5 flex items-center justify-between gap-3 hover:bg-muted/20 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
                          {user.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-xs text-foreground truncate">
                            {user.name}
                          </p>
                          <p className="text-[11px] text-muted-foreground font-mono truncate">
                            {user.email_id}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {user.is_totp_enabled && (
                          <span
                            className="hidden sm:inline-flex text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20"
                            title="2FA Active"
                          >
                            2FA
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={unlinkingUserId === user.id}
                          onClick={() => handleUnlink(user.id)}
                          className="h-8 px-2.5 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        >
                          {unlinkingUserId === user.id ? "Unlinking..." : "Unlink"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 3: Operational Settings & Audit */}
          {activeTab === "settings" && (
            <div className="space-y-4 animate-in fade-in-50 duration-150">
              <div className="p-4 rounded-2xl border border-border/70 dark:border-zinc-800 bg-card space-y-2">
                <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  Audit & Operational Metadata
                </h4>
                <dl className="divide-y divide-border/50">
                  <DetailRow label="System Identifier">
                    <span className="font-mono text-[11px]">{client.id}</span>
                  </DetailRow>
                  <DetailRow label="Onboarded At">
                    <span className="font-mono text-[11px]">
                      {new Date(client.created_at).toLocaleString()}
                    </span>
                  </DetailRow>
                  <DetailRow label="Last Profile Update">
                    <span className="font-mono text-[11px]">
                      {new Date(client.updated_at).toLocaleString()}
                    </span>
                  </DetailRow>
                </dl>
              </div>

              {/* Danger Zone */}
              <div className="p-4 rounded-2xl border border-destructive/20 bg-destructive/5 space-y-3">
                <h4 className="text-xs font-bold text-destructive flex items-center gap-1.5">
                  <ShieldAlert className="h-4 w-4" />
                  Lifecycle Danger Zone
                </h4>
                <p className="text-xs text-muted-foreground">
                  Deactivating this client organization will automatically disable all linked client portal users
                  and revoke active sessions across the gateway.
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onToggleStatus(client)}
                    className="text-xs h-8"
                  >
                    {client.status === "active" ? "Suspend Organization" : "Reactivate Organization"}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDelete(client)}
                    className="text-xs h-8 gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete Organization
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
